import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';

import { Block, Paragraph } from '@uncinc/react-drupal-core';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { DEFAULT_LANGUAGE } from '../../../../config/constants';
import { NodeProject } from '../../../../types/uncinc_profile/nodes';
import {
  Breadcrumbs,
  ProjectHero,
  ProjectMap,
  RelatedContentByProject,
  WysiwygField,
} from '../../../../components';

interface NodeProjectSchoolProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeProject>;
  className?: string;
}

const NodeProjectSchool: React.FC<NodeProjectSchoolProps> = ({ entity, className = '', ...props }) => {
  const node = entity.toJS();
  const hasTextualContent = node.body.length > 0 || node.field_paragraphs.length > 0;
  const classes = classNames('NodeProject NodeProjectSchool', className, {
    'NodeProject--has-textual-content': hasTextualContent,
  });
  const heroColumnClasses = hasTextualContent ? 'col-lg-5 col-xl-6' : 'col-12';
  const contactFormBlockEntity = entity.getIn(['field_contact_form_block', 0, 'entity'], Immutable.Map());

  const status = node.field_status[0]?.entity ? node.field_status[0].entity.name[0].value : '';
  const types =
    node.field_type ? node.field_type.map(({ entity: term }) => term.name[0].value) : [];

  return (
    <article itemType="http://schema.org/WebPage" className={classes} {...props}>
      <Container>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs />
          </div>
        </div>

        <div className="row">
          <div className={heroColumnClasses}>
            <ProjectHero
              logo={node.field_media_item[0]}
              title={node.title[0].value}
              status={status}
              schoolTypes={types}
              location={node.field_location[0].entity}
              url={node.field_link?.[0]?.alias || node.field_link?.[0]?.uri}
            />
          </div>

          {hasTextualContent && (
            <div className="col-lg-7 col-xl-6">
              {node.body.length > 0 && (
                <div className="content-section mt-lg-6 mb-0">
                  <WysiwygField
                    className="body--large"
                    property="schema:text"
                    html={node.body?.[0]?.processed}
                  />
                </div>
              )}

              {node.field_paragraphs.length > 0 && (
                <div>
                  {node.field_paragraphs.map(({ target_id: id, entity: paragraph }) => {
                    const language = paragraph.langcode[0].value || DEFAULT_LANGUAGE;

                    return (
                      <Paragraph
                        key={`paragraph:${id}:${language}`}
                        id={id}
                        entity={Immutable.Map(paragraph)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </Container>

      <RelatedContentByProject
        projectId={node.nid[0].value}
      />

      <Container>
        <div className="row">
          <div className="col-12">
            <div className="content-section">
              <ProjectMap defaultMarkerId={node.nid[0].value} />
            </div>
          </div>
        </div>

        {contactFormBlockEntity && (
          <Block entity={contactFormBlockEntity} />
        )}
      </Container>
    </article>
  );
};

export default NodeProjectSchool;
